/**
 * Service that handles all the WebRTC logic.
 */
import livestreamActions from '../../actions/LivestreamActions';
import storeManager from '../../store/StoreManager';
import MediaStreamConfigurableStatic from '../../webRTCLivestream/Classes/Call/MediaStreamConfigurableStatic';
import CallConfig from '../../webRTCLivestream/Configs/CallConfig';
import MediaStreamManager from '../../webRTCLivestream/Classes/MediaStreamManager';
import RecordManager from '../../webRTCLivestream/Classes/Record/RecordManager';
import StudentApp from '../../webRTCLivestream/Components/Apps/StudentApp';
import apiService from '../api/ApiService';
import liveSessionCodecAdapter from '../../services/livesession/LiveSessionCodecAdapter';
import { startLiveSessionError } from './HardwareAccessCheckConstants';
import sessionReader from '../../store/readHelpers/SessionReader';
import marketplacePropertiesService from '../MarketplacePropertiesService';
import liveSessionLogger from './LiveSessionLogger';
import p2pConnectionStore from './P2PConnectionStore';
import proctorAudioService from './ProctorAudioService';
import RecordingCallConfig from '../../webRTCLivestream/Configs/RecordingCallConfig';
import webcamHealthChecker from './webcamHealthChecker';
import micHealthChecker from './micHealthChecker';

const { liveSessionConnectionStatus } = livestreamActions;
CallConfig.isCaller = true;

const P2PStatus = {
    CONNECTED: 'connected', // The connection has become fully connected
    DISCONNECTED: 'disconnected',
    FAILED: 'failed', // One or more transports has terminated unexpectedly or in an error
    CLOSED: 'closed', // The connection has been closed
};

// Video configuration on the candidate video expected/preferred by a particular proctor.
const VideoExpectation = {
    LOW_DEFINITION: 'low_definition',
};

// Video configuration on the candidate video expected/preferred by a particular proctor.
const AudioExpectation = {
    MUTED: 'muted',
};
let GRANTED_LOCAL_STREAM = null;
let GRANTED_LOCAL_STREAM_LOW_DEFINITION = null; // Low definition
let USER_MEDIA_SETTINGS = {};
let RECORDING_LOCAL_STREAM = null;
let RECORDING_MEDIA_SETTINGS = null;

const MAX_ICE_CANDIDATE_GATHERING_WAITING_TIME = 6 * 1000; // RP-1628: The max time we wait for all the ice candidates to be fetched.

let ICE_SERVERS = null;
let studentApp;

const liveSessioner = {
    requestHardwareAccess(webcamDeviceId = null, audioDeviceId = null) {
        if (GRANTED_LOCAL_STREAM) {
            // do not request access if it's already granted
            return;
        }

        USER_MEDIA_SETTINGS = {
            video: webcamDeviceId ? { ...CallConfig.constraints.video(), deviceId: { exact: webcamDeviceId } } : CallConfig.constraints.video(),
            audio: audioDeviceId ? { ...CallConfig.constraints.audio(), deviceId: { exact: audioDeviceId } } : CallConfig.constraints.audio(),
        };

        navigator.mediaDevices.getUserMedia(USER_MEDIA_SETTINGS).then((stream) => {
            GRANTED_LOCAL_STREAM = stream;

            navigator.mediaDevices.enumerateDevices()
                .then((devices) => {
                    // Start webcam health check only if enabled and there is at least one video device
                    if (sessionReader.isRemoteProctoringWebcamHealthCheckEnabled() &&
                        devices.some(device => device.kind === 'videoinput')) {
                        webcamHealthChecker.startWebcamHealthCheck(stream);
                    }

                    // Check if mic health check is enabled and if there's at least one audio input device
                    if (sessionReader.isRemoteProctoringMicHealthCheckEnabled()) {
                        const hasAudioInputDevices = devices.some(device => device.kind === 'audioinput');
                        if (hasAudioInputDevices) {
                            // Start monitoring mic health
                            micHealthChecker.startMonitoring(stream);
                        }
                    }

                    storeManager.dispatch(
                        livestreamActions.grantHardwareAccess(devices)
                    );
                })
                .catch(() => {
                    storeManager.dispatch(
                        livestreamActions.rejectHardwareAccess()
                    );
                });
        }).catch(() => {
            storeManager.dispatch(
                livestreamActions.rejectHardwareAccess()
            );
        });

        if (RECORDING_LOCAL_STREAM) {
            return;
        }

        // Refactored: Only honor the BE flag enableEnhancedHttpRecording
        if (sessionReader.isEnhancedProctoringRecordingEnabledForTest()) {
            RecordingCallConfig.constraints.setRecordingConstraints();
            RECORDING_MEDIA_SETTINGS = {
                video: webcamDeviceId ?
                    { ...RecordingCallConfig.constraints.video(), deviceId: { exact: webcamDeviceId } }
                    : RecordingCallConfig.constraints.video(),
                audio: audioDeviceId ?
                    { ...RecordingCallConfig.constraints.audio(), deviceId: { exact: audioDeviceId } }
                    : RecordingCallConfig.constraints.audio(),
            };

            navigator.mediaDevices.getUserMedia(RECORDING_MEDIA_SETTINGS).then((stream) => {
                RECORDING_LOCAL_STREAM = stream;
            }).catch(() => {
                storeManager.dispatch(livestreamActions.rejectHardwareAccess());
            });
        }
    },

    /**
     * Starts streaming to the Recorder.
     * @param audioStreaming
     * @param webcamStreaming
     */
    async startRecorderStreaming(audioStreaming, webcamStreaming) {
        let streamToUse = null;
        let configToUse = null;
        if (RECORDING_LOCAL_STREAM) {
            RecordingCallConfig.audioStreaming = audioStreaming;
            RecordingCallConfig.webcamStreaming = webcamStreaming;
            streamToUse = RECORDING_LOCAL_STREAM;
            configToUse = RecordingCallConfig;
        } else {
            CallConfig.audioStreaming = audioStreaming;
            CallConfig.webcamStreaming = webcamStreaming;
            streamToUse = GRANTED_LOCAL_STREAM;
            configToUse = CallConfig;
        }

        const streamManager = new MediaStreamManager();
        studentApp = new StudentApp(
            streamManager,
            new RecordManager(streamManager),
        );

        const configurableStream = new MediaStreamConfigurableStatic(streamToUse, configToUse);
        studentApp.getMediaCommandForRecording().runMediaCommand(configurableStream);
    },

    /**
     * Makes the candidate discoverable by proctor.
     * @param onSessionLiveSessionStarted
     * @param onSessionLiveSessionFailed
     */
    async startLiveSessionStreaming(
        assessmentRunId,
        audioMaxBitrateInKbps,
        videoMaxBitrateInKbps,
        onSessionLiveSessionStarted,
        onSessionLiveSessionFailed,
    ) {
        try {
            const generateNTSTokenResponse = await apiService.generateNTSToken(assessmentRunId);
            console.log(`generated NTS Token response: ${JSON.stringify(generateNTSTokenResponse)}`);
            ICE_SERVERS = generateNTSTokenResponse.iceServers;

            CallConfig.rtpSettings.videoMaxBitrate = videoMaxBitrateInKbps * 1000;
            CallConfig.rtpSettings.audioMaxBitrate = audioMaxBitrateInKbps * 1000;

            const response = await apiService.startLiveSession(assessmentRunId);
            if (this.isLiveSessionStarted(response)) {
                storeManager.dispatch(
                    livestreamActions.setLiveSession(response.assessmentRunSessionId)
                );
                console.log('LiveSession enacted');
                onSessionLiveSessionStarted();
            } else {
                const errorCode = this.isMissingSession(response)
                    ? startLiveSessionError.MISSING_SESSION
                    : startLiveSessionError.DEFAULT;
                onSessionLiveSessionFailed(errorCode);
            }
        } catch (e) {
            console.error(e);
            onSessionLiveSessionFailed(startLiveSessionError.DEFAULT);
        }
    },

    isLiveSessionStarted(response) {
        return !!response.assessmentRunSessionId;
    },

    isMissingSession(response) {
        return !response.success && response.error === 'missingSession';
    },

    setWebCamDeviceId(webcamDeviceId) {
        if (!USER_MEDIA_SETTINGS.video.deviceId) {
            USER_MEDIA_SETTINGS = {
                ...USER_MEDIA_SETTINGS,
                video: {
                    ...USER_MEDIA_SETTINGS.video,
                    deviceId: { exact: webcamDeviceId },
                },
            };
        }
    },

    switchDevices(webcamDeviceId, audioDeviceId, prevWebcamDeviceId, prevAudioDeviceId) {
        const webcamConstraints = { ...USER_MEDIA_SETTINGS.video, deviceId: { exact: webcamDeviceId } };
        const audioConstraints = { ...USER_MEDIA_SETTINGS.audio, deviceId: { exact: audioDeviceId } };

        USER_MEDIA_SETTINGS = {
            video: webcamDeviceId ? webcamConstraints : USER_MEDIA_SETTINGS.video,
            audio: audioDeviceId ? audioConstraints : USER_MEDIA_SETTINGS.audio,
        };
        navigator.mediaDevices.getUserMedia(USER_MEDIA_SETTINGS).then((stream) => {
            GRANTED_LOCAL_STREAM = stream;

            // Only run health check if webcam device actually changed
            if (
                sessionReader.isRemoteProctoringWebcamHealthCheckEnabled() &&
                webcamDeviceId &&
                webcamDeviceId !== prevWebcamDeviceId
            ) {
                webcamHealthChecker.resetAndStartWebcamHealthCheck(stream);
            }

            storeManager.dispatch(
                livestreamActions.switchDevices(webcamDeviceId, audioDeviceId)
            );

            // Only restart mic health check if audio device actually changed
            if (
                sessionReader.isRemoteProctoringMicHealthCheckEnabled() &&
                audioDeviceId &&
                audioDeviceId !== prevAudioDeviceId
            ) {
                micHealthChecker.resetAndRestart(stream);
            }
        }).catch(() => {
            storeManager.dispatch(
                livestreamActions.rejectHardwareAccess()
            );
        });
    },

    getGrantedStream() {
        return GRANTED_LOCAL_STREAM;
    },

    stopStream() {
        if (!GRANTED_LOCAL_STREAM) {
            return;
        }

        GRANTED_LOCAL_STREAM.getTracks().forEach(track => track.stop());
        GRANTED_LOCAL_STREAM = null;

        storeManager.dispatch(
            livestreamActions.closeHardwareAccess(),
        );

        if (!RECORDING_LOCAL_STREAM) {
            return;
        }

        RECORDING_LOCAL_STREAM.getTracks().forEach(track => track.stop());
        RECORDING_LOCAL_STREAM = null;
    },

    hasAnyRemoteStream(messagerId, liveSessionTimestamp) {
        return p2pConnectionStore.hasRemoteAudioStream(messagerId, liveSessionTimestamp)
            || p2pConnectionStore.hasRemoteWebcamStream(messagerId, liveSessionTimestamp);
    },

    setRemoteStream(event, messagerId, liveSessionTimestamp) {
        console.log(`${(new Date()).toISOString()}:setRemoteStream`, event, messagerId, liveSessionTimestamp);
        if (event.track.kind === 'video') {
            p2pConnectionStore.setRemoteWebcamStream(messagerId, liveSessionTimestamp, event.streams[0]);
        } else if (event.track.kind === 'audio') {
            p2pConnectionStore.setRemoteAudioStream(messagerId, liveSessionTimestamp, event.streams[0]);
        }
    },

    setExaminerRemoteStream(stream) {
        if (sessionReader.isWebRTCBasedRemoteSpeakingEnabled() && marketplacePropertiesService.isProctorAudioRecordingEnabled()) {
            const streamManager = studentApp?.getStreamManager();
            if (streamManager) {
                streamManager.setRemoteStream(stream);
            }
        }
    },

    // makes sure we have a user video stream with a low resolution video ready to share.
    async ensureUserMediaWithLowDefinitionVideo() {
        if (!GRANTED_LOCAL_STREAM_LOW_DEFINITION) {
            const webcamConstraints = {
                ...USER_MEDIA_SETTINGS.video,
                width: 426,
                height: 240,
            };

            GRANTED_LOCAL_STREAM_LOW_DEFINITION = await navigator.mediaDevices.getUserMedia({
                video: webcamConstraints,
                audio: USER_MEDIA_SETTINGS.audio,
            });
        }
        return GRANTED_LOCAL_STREAM_LOW_DEFINITION;
    },

    loadConnectionWithLocalTracks(
        rtcPeerConnection,
        videoExpectation,
        audioExpectation
    ) {
        console.log(`${(new Date()).toISOString()}:loadConnectionWithLocalTracks`, rtcPeerConnection);


        if (CallConfig.webcamStreaming) {
            if (videoExpectation === VideoExpectation.LOW_DEFINITION) {
                console.log(`${(new Date()).toISOString()}:Sharing low definition video`);
                GRANTED_LOCAL_STREAM_LOW_DEFINITION.getVideoTracks().forEach((track) => {
                    rtcPeerConnection.addTrack(track, GRANTED_LOCAL_STREAM_LOW_DEFINITION);
                });
            } else {
                console.log(`${(new Date()).toISOString()}:Sharing high definition video`);
                GRANTED_LOCAL_STREAM.getVideoTracks().forEach((track) => {
                    rtcPeerConnection.addTrack(track, GRANTED_LOCAL_STREAM);
                });
            }
        }

        if (CallConfig.audioStreaming && audioExpectation !== AudioExpectation.MUTED) {
            console.log(`${(new Date()).toISOString()}:Sharing audio stream`);
            GRANTED_LOCAL_STREAM.getAudioTracks().forEach((track) => {
                rtcPeerConnection.addTrack(track, GRANTED_LOCAL_STREAM);
            });
        }
    },

    registerOwnIceCandidate(event, messagerId, liveSessionTimestamp) {
        console.log(`${(new Date()).toISOString()}:registerOwnIceCandidate`, messagerId, liveSessionTimestamp);

        // Gathering of ice_candidates completed
        if (event.candidate === null) {
            this.getOwnIceCandidatesPromise(messagerId, liveSessionTimestamp).gatherOwnIceCandidatesResolve(true);
        } else {
            const localIceCandidate = {
                sdpMLineIndex: event.candidate.sdpMLineIndex,
                candidate: event.candidate.candidate,
            };
            p2pConnectionStore.addLocalIceCandidate(messagerId, liveSessionTimestamp, localIceCandidate);
        }
    },

    isNewOrLatestConnectionOfferFromProctor(messagerId, liveSessionTimestamp) {
        const latestKey = p2pConnectionStore.getLatestMessagerConnectionKey(messagerId);
        if (!latestKey) {
            // is first offer for a brand new proctor.
            return true;
        } else if (p2pConnectionStore.isLastMessagerConnectionKey(messagerId, liveSessionTimestamp)) {
            // existing offer is also the current latest one, thus this is a recovery offer.
            return true;
        }
        // incoming offer from same proctor has a *newer* liveSessionTimestamp and thus should be allowed to be attended.
        const liveSessionTimestampLatest = p2pConnectionStore.parseLiveSessionTimestampFromKey(latestKey);
        return liveSessionTimestamp > liveSessionTimestampLatest;
    },

    handleConnectionChange(rtcPeerConnection, messagerId, liveSessionTimestamp) {
        const status = rtcPeerConnection.iceConnectionState;
        console.log(`LiveSession:handleConnectionChange:${status}`, messagerId, liveSessionTimestamp);

        if (P2PStatus.DISCONNECTED === status) {
            // We only need to enact the timeout if we are the latest connection for this proctor.
            // Use case: If we were in Watch and switch to Call, then the status=online of the new
            // connection will/can hit before the status=disconnected of the previous one.
            if (!p2pConnectionStore.isLastMessagerConnectionKey(messagerId, liveSessionTimestamp)) {
                return;
            }

            const waitingTime = marketplacePropertiesService.getMaxLiveSessionRecoveryTimeSecs();
            const recoveryTimeoutId = setTimeout(() => {
                // eviction time, but first check if after all this time the timeout is still relevant or stale (if there
                // is a newer p2p connection for this proctor).
                if (!p2pConnectionStore.isLastMessagerConnectionKey(messagerId, liveSessionTimestamp)) {
                    return;
                }
                console.log(`${(new Date()).toISOString()}:LiveSession:Evict:We were unable to recover the connection. Evicting proctor.`,
                    messagerId, liveSessionTimestamp);
                apiService.evictLiveSessionProctor(messagerId)
                    .then((response) => {
                        if (response.errorReason) {
                            console.log(`${(new Date()).toISOString()}:LiveSession:Evict:Unsuccessful due to illegal session status`);
                        }
                        storeManager.dispatch(
                            livestreamActions.removeOfferer(messagerId)
                        );
                    })
                    .catch(() => {
                        console.log(`${(new Date()).toISOString()}:LiveSession:Evict:Unsuccessful due to response fail.`);
                        // Scenario MVP2:6.D: Candidate is prob offline, removing offerer anyway.
                        // If in a call, candidate will stay paused.
                        storeManager.dispatch(
                            livestreamActions.removeOfferer(messagerId)
                        );
                    });
            }, waitingTime * 1000);

            p2pConnectionStore.setP2PRecoveryTimeoutId(messagerId, liveSessionTimestamp, recoveryTimeoutId);
            p2pConnectionStore.setP2PDisconnection(messagerId, liveSessionTimestamp, {
                wasCandidateOnlineUponDisconnection: window.navigator.onLine,
            });

            storeManager.dispatch(
                livestreamActions.setConnectionStatus(
                    messagerId,
                    liveSessionConnectionStatus.RECONNECTING,
                    liveSessionTimestamp
                )
            );
        } else if (P2PStatus.CONNECTED === status) {
            const disconnectionInfo = p2pConnectionStore.getP2PDisconnection(messagerId, liveSessionTimestamp);
            if (disconnectionInfo?.wasCandidateOnlineUponDisconnection === true) {
                // P2P connection is up, we do not need to wait for the push channel to be restored because we were online
                // when disconnection occurred and push channel can be presumed to be healthy.
                const assessmentRunId = sessionReader.getAssessmentRunId(true);
                apiService.sendLiveSessionBackOnlineNotice(assessmentRunId).catch(() => {
                    console.log(`${(new Date()).toISOString()}:LiveSession:BackOnlineNotice:Unsuccessful due to response fail.`);
                });
            } else {
                // P2P connection is up, but we need to wait until the push channel is back on.
                // We will have to wait for the notice sent at AWSIOTService.subscribeLiveSessionTopics upon socket renewal.
            }
        }
    },

    resetOfferPromise(messagerId, liveSessionTimestamp) {
        const p2pOfferPromise = {};
        p2pOfferPromise.offerPromise = new Promise((newOfferPromiseResolve) => {
            p2pOfferPromise.offerPromiseResolve = newOfferPromiseResolve;
        });
        p2pConnectionStore.setP2POfferPromise(messagerId, liveSessionTimestamp, p2pOfferPromise);
    },

    resetGatherOwnIceCandidatesPromise(messagerId, liveSessionTimestamp) {
        const promiseInfo = {};
        p2pConnectionStore.resetLocalIceCandidates(messagerId, liveSessionTimestamp);
        p2pConnectionStore.setP2POwnCandidatesPromise(messagerId, liveSessionTimestamp, promiseInfo);

        promiseInfo.gatherOwnIceCandidatesPromise = new Promise((
            gatherOwnIceCandidatesResolve,
            gatherOwnIceCandidatesReject,
        ) => {
            promiseInfo.gatherOwnIceCandidatesResolve = gatherOwnIceCandidatesResolve;
            promiseInfo.gatherOwnIceCandidatesReject = gatherOwnIceCandidatesReject;
        });

        // We were able to gather all ice-candidates in a reasonable time.
        promiseInfo.gatherOwnIceCandidatesPromise.then(() => {
            const localIceCandidates = p2pConnectionStore.getLocalIceCandidate(messagerId, liveSessionTimestamp);
            console.log(`${(new Date()).toISOString()}:LiveSessioner:sendLiveSessionIceCandidates`,
                messagerId, liveSessionTimestamp, localIceCandidates);

            if (!localIceCandidates) {
                liveSessionLogger.reportError(messagerId, 'do not send LiveSessionIceCandidates - no ice candidates');
                return true;
            }

            return apiService.sendLiveSessionIceCandidates(localIceCandidates, messagerId);
        }).catch((reason) => { // We send what we have so far.
            const localIceCandidates = p2pConnectionStore.getLocalIceCandidate(messagerId, liveSessionTimestamp);
            console.log(`${(new Date()).toISOString()}:LiveSessioner:sendLiveSessionIceCandidates:${reason}`,
                messagerId, liveSessionTimestamp, localIceCandidates);

            if (!localIceCandidates) {
                liveSessionLogger.reportError(messagerId, `do not send already collected LiveSessionIceCandidates - no ice candidates. ${reason}`);
                return true;
            }
            return apiService.sendLiveSessionIceCandidates(localIceCandidates, messagerId);
        });
        return promiseInfo;
    },

    getOwnIceCandidatesPromise(messagerId, liveSessionTimestamp) {
        return p2pConnectionStore.getP2POwnCandidatesPromise(messagerId, liveSessionTimestamp);
    },

    getOrCreateP2P(
        messagerId,
        liveSessionTimestamp,
        videoExpectation,
        audioExpectation
    ) {
        let rtcPeerConnection = p2pConnectionStore.getP2PConnection(messagerId, liveSessionTimestamp);
        if (!rtcPeerConnection) {
            rtcPeerConnection = new RTCPeerConnection({
                iceServers: ICE_SERVERS,
            });
            p2pConnectionStore.addP2PConnection(messagerId, liveSessionTimestamp, rtcPeerConnection);

            this.loadConnectionWithLocalTracks(
                rtcPeerConnection,
                videoExpectation,
                audioExpectation
            );
            this.resetOfferPromise(messagerId, liveSessionTimestamp);
        }
        return rtcPeerConnection;
    },

    getSessionPromise(messagerId, liveSessionTimestamp) {
        return p2pConnectionStore.getP2POfferPromise(messagerId, liveSessionTimestamp);
    },

    async receiveOffer(
        messagerId,
        offer,
        proctoringMode,
        liveSessionTimestamp,
        videoExpectation,
        audioExpectation
    ) {
        console.log(`${(new Date()).toISOString()}:LiveSessioner:receiveOffer`, offer, messagerId, proctoringMode);
        liveSessionLogger.addEvent(messagerId, `receiveOffer - ${offer.type}, ${proctoringMode}`);

        storeManager.dispatch(
            livestreamActions.setOfferer(
                messagerId,
                proctoringMode,
                (new Date()).getTime(),
                liveSessionTimestamp
            )
        );

        try {
            if (videoExpectation === VideoExpectation.LOW_DEFINITION) {
                await this.ensureUserMediaWithLowDefinitionVideo();
            }

            const rtcPeerConnection = this.getOrCreateP2P(
                messagerId,
                liveSessionTimestamp,
                videoExpectation,
                audioExpectation
            );

            rtcPeerConnection.onnegotiationneeded = (event) => {
                console.log(`${(new Date()).toISOString()}:event:onnegotiationneeded`, event);
                liveSessionLogger.addEvent(messagerId, 'negotiation needed');
            };

            rtcPeerConnection.ontrack = (event) => {
                this.setRemoteStream(event, messagerId, liveSessionTimestamp);
            };

            rtcPeerConnection.onicecandidate = (event) => {
                this.registerOwnIceCandidate(event, messagerId, liveSessionTimestamp);
            };

            rtcPeerConnection.onicegatheringstatechange = () => {
                console.log(`${(new Date()).toISOString()}:New ice-gathering-state`,
                    messagerId, liveSessionTimestamp, rtcPeerConnection.iceGatheringState);
                liveSessionLogger.addEvent(messagerId, 'new ice-gathering-state');

                if (rtcPeerConnection.iceGatheringState === 'gathering') {
                    const promiseCallbacks = this.resetGatherOwnIceCandidatesPromise(messagerId, liveSessionTimestamp);

                    setTimeout(() => {
                        // give up on waiting for more and we go with what we have so far
                        promiseCallbacks.gatherOwnIceCandidatesReject('Timeout waiting for ice-candidates');
                    }, MAX_ICE_CANDIDATE_GATHERING_WAITING_TIME);
                }
            };

            p2pConnectionStore.setP2POffer(messagerId, liveSessionTimestamp, offer);
            await rtcPeerConnection.setRemoteDescription(new RTCSessionDescription(offer));
            await rtcPeerConnection.setLocalDescription(await rtcPeerConnection.createAnswer());

            // bitrate handling
            await CallConfig.rtpSettings.both(rtcPeerConnection);

            // adapting video codec priority based on capabilities.
            liveSessionCodecAdapter.adaptVideoCodec(rtcPeerConnection);

            // we declare the remote Offer registration as ready. This triggers registration of any pending remote ice_candidates.
            const sessionPromise = this.getSessionPromise(messagerId, liveSessionTimestamp);
            sessionPromise.offerPromiseResolve();

            await apiService.sendLiveSessionAnswer(rtcPeerConnection.localDescription, messagerId);

            if (p2pConnectionStore.clearPendingRecoveryWaitingTimeouts(messagerId)) {
                storeManager.dispatch(
                    livestreamActions.setConnectionStatus(
                        messagerId,
                        liveSessionConnectionStatus.CONNECTED,
                        liveSessionTimestamp
                    )
                );
            }
        } catch (e) {
            console.log(`${(new Date()).toISOString()}:LiveSessioner:receiveOffer:ERROR`, e);
            liveSessionLogger.reportError(messagerId, `receiveOffer:ERROR, ${e}`);
            storeManager.dispatch(
                livestreamActions.deemSessionSetupFailed(messagerId)
            );
        }
    },

    receiveProctoringMode(messagerId, proctoringMode) {
        console.log(`${(new Date()).toISOString()}:receiveProctoringMode`, messagerId, proctoringMode);
        liveSessionLogger.addEvent(messagerId, `receiveProctoringMode - ${proctoringMode}`);

        storeManager.dispatch(
            livestreamActions.setProctoringMode(
                messagerId,
                proctoringMode,
                (new Date()).getTime() // registering the time at which we received the proctoring_mode update message.
            )
        );

        if (marketplacePropertiesService.isProctorAudioRecordingEnabled()) {
            proctorAudioService.addOrRemoveProctorStreamToFromP2PConnection(messagerId, proctoringMode, studentApp);
        }
    },

    receiveRemoteIceCandidates(messagerId, iceCandidates, liveSessionTimestamp) {
        console.log(`${(new Date()).toISOString()}:receiveRemoteIceCandidates`, iceCandidates, messagerId, liveSessionTimestamp);
        liveSessionLogger.addEvent(messagerId, 'receive remote ice candidates');

        const sessionPromise = this.getSessionPromise(messagerId, liveSessionTimestamp);
        // If we did not have a promise registered yet, then it means we are getting the remote ice_candidates
        // before the very first Offer has arrived. We will just have to retry later.
        if (!sessionPromise) {
            console.log(`${(new Date()).toISOString()}:IceCandidates arrived before the very first offer. Retry later.`,
                iceCandidates, messagerId, liveSessionTimestamp);
            setTimeout(() => {
                this.receiveRemoteIceCandidates(messagerId, iceCandidates, liveSessionTimestamp);
            }, 1000);
            return;
        }

        // schedule the remote ice_candidates for loading once the Offer is ready.
        sessionPromise.offerPromise.then(() => {
            console.log(`${(new Date()).toISOString()}:Adding remote iceCandidates now that remote Offer is ready`);
            const rtcPeerConnection = p2pConnectionStore.getP2PConnection(messagerId, liveSessionTimestamp);

            iceCandidates.forEach((iceCandidate) => {
                p2pConnectionStore.addRemoteIceCandidate(messagerId, liveSessionTimestamp, iceCandidate);
                // ICE candidate configuration.
                const candidate = new RTCIceCandidate(iceCandidate);
                rtcPeerConnection.addIceCandidate(candidate)
                    .catch((error) => {
                        console.log(`${(new Date()).toISOString()}:addIceCandidate:error`, error, candidate);
                        liveSessionLogger.addEvent(messagerId, `add ice candidate error - ${error}`);
                    });
            });

            rtcPeerConnection.oniceconnectionstatechange = () => {
                this.handleConnectionChange(rtcPeerConnection, messagerId, liveSessionTimestamp);
            };
        }).then(() => {
            // Now that we have consumed the offerPromise, we leave it ready for the next time it is needed.
            sessionPromise.offerPromise = new Promise((newOfferPromiseResolve) => {
                sessionPromise.offerPromiseResolve = newOfferPromiseResolve;
            });
        });
    },

    receiveLeavingProctor(messagerId) {
        console.log(`${(new Date()).toISOString()}:receiveLeavingProctor`, messagerId);
        liveSessionLogger.addEvent(messagerId, 'receive leaving proctor');
        this.sanitizeLiveSessionForProctor(messagerId);

        storeManager.dispatch(livestreamActions.removeOfferer(messagerId));
    },

    // clears all connections for this messagerId
    sanitizeLiveSessionForProctor(messagerId) {
        p2pConnectionStore.closeP2PConnectionsByMessagerId(messagerId);
        p2pConnectionStore.clearPendingRecoveryWaitingTimeouts(messagerId);
    },

    sanitizeLiveSessionsUponDelivery() {
        console.log(`${(new Date()).toISOString()}:sanitizeLiveSessionsUponDelivery`);

        p2pConnectionStore.resolveAllConnectionMessagerIds().forEach((messagerId) => {
            p2pConnectionStore.clearPendingRecoveryWaitingTimeouts(messagerId);
            storeManager.dispatch(livestreamActions.removeOfferer(messagerId));
        });

        p2pConnectionStore.closeAllP2PConnections();
        p2pConnectionStore.sanitizeStore();
    },
};

export default liveSessioner;
